import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PasswordInput from '../PasswordInput';
import { trackEvent } from '../../utils/analytics';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState<{email?: string, password?: string}>({});
  const [loginError, setLoginError] = useState<string | null>(null);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const { login, googleLogin, microsoftLogin, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthError = searchParams.get('error') === 'work_email_required'
    ? 'A work or organizational email is required to create an account. Please try again with your work email.'
    : null;
  const returnTo = searchParams.get('returnTo') || '/dashboard';
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const errorBannerRef = useRef<HTMLDivElement>(null);
  
  // Focus password field after login error
  useEffect(() => {
    if (loginError && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [loginError]);
  
  const validateForm = (): boolean => {
    const errors: {email?: string, password?: string} = {};
    let isValid = true;
    
    if (!email) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is not valid';
      isValid = false;
    }
    
    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setVerificationRequired(false);
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await login(email, password);
      trackEvent('auth_login_success', { method: 'password' });
      navigate(returnTo);
    } catch (err: any) {
      // Clear password but keep email for retry
      setPassword('');
      // Set local error state instead of relying on context redirect
      const errorMessage = err.message || 'Invalid email or password. Please try again.';
      setLoginError(errorMessage);
      setVerificationRequired(err.code === 'email_verification_required');
    }
  };
  
  const handleGoogleLogin = async () => {
    try {
      await googleLogin();
      // Redirect is handled by the googleLogin function
    } catch (err) {
      // Error is handled by the auth context
    }
  };
  
  const handleMicrosoftLogin = async () => {
    try {
      await microsoftLogin();
      // Redirect is handled by the microsoftLogin function
    } catch (err) {
      // Error is handled by the auth context
    }
  };
  
  return (
    <>
      {(loginError || oauthError) && (
        <div 
          ref={errorBannerRef}
          className="login-error-banner"
          role="alert"
          aria-live="assertive"
          style={{
            backgroundColor: 'var(--color-error-bg)',
            border: '1px solid var(--color-error)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: 'var(--color-danger)',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 20 20" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ flexShrink: 0 }}
          >
            <path 
              fillRule="evenodd" 
              clipRule="evenodd" 
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" 
              style={{ fill: 'var(--color-error)' }}
            />
          </svg>
          <span>
            {loginError || oauthError}
            {loginError && verificationRequired && (
              <> <Link to="/auth/verify-email" className="verification-link">
                Verify email <span aria-hidden="true">→</span>
              </Link></>
            )}
          </span>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            type="email"
            id="email"
            className="form-input"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (loginError) { setLoginError(null); setVerificationRequired(false); }
            }}
            placeholder="Enter your email"
          />
          {formErrors.email && <div className="form-error">{formErrors.email}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="password" className="form-label">Password</label>
          <PasswordInput
            id="password"
            ref={passwordInputRef}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (loginError) { setLoginError(null); setVerificationRequired(false); }
            }}
            placeholder="Enter your password"
          />
          {formErrors.password && <div className="form-error">{formErrors.password}</div>}
        </div>
        
        <div className="forgot-password">
          <Link to="/forgot-password">Forgot password?</Link>
        </div>
        
        <button type="submit" className="form-button" disabled={loading}>
          {loading ? 'Logging in...' : 'Log In'}
        </button>
      </form>
      
      <div className="signup-prompt">
        Don't have an account? <Link to="/auth/register">Sign up</Link>
      </div>
    </>
  );
};

export default LoginForm;
